import React from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, DollarSign, Zap, Info } from 'lucide-react'
import clsx from 'clsx'

interface RoiData {
  totalActions: number;
  totalOpened: number;
  estimatedValue: number;
  valuePerAction: number;
  frictionScore: number;
  labels: { headline: string; subline: string };
}

const WARM_CARD = '#FDFCF8'
const WARM_BORDER = '#e0d9cc'

export function RoiReport({ data, loading }: { data: RoiData | null; loading: boolean }) {
  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-32 bg-warm-inset rounded-2xl" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-24 bg-warm-inset rounded-2xl" />
          <div className="h-24 bg-warm-inset rounded-2xl" />
        </div>
      </div>
    )
  }

  if (!data) return null

  const savings = data.estimatedValue.toLocaleString()

  return (
    <div className="space-y-4">
      {/* Hero ROI Card */}
      <div 
        className="rounded-2xl border p-6 shadow-card relative overflow-hidden bg-white"
        style={{ borderColor: WARM_BORDER }}
      >
        <div className="absolute top-0 right-0 p-4 opacity-10">
          <TrendingUp size={80} className="text-brand-teal" />
        </div>
        
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-brand-teal/10 text-brand-teal text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-brand-teal/20">
              Behavioral ROI
            </span>
          </div>
          
          <h3 className="text-2xl font-bold text-ink mb-1" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            {data.labels.headline}
          </h3>
          <p className="text-sm text-ink-soft mb-6">{data.labels.subline}</p>
          
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-brand-teal">${savings}</span>
            <span className="text-xs text-ink-muted font-medium">Estimated Value Created</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div 
          className="rounded-2xl border p-5 bg-white shadow-sm"
          style={{ borderColor: WARM_BORDER }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-ink-soft">Friction Score</p>
            <Zap size={14} className="text-amber-500" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-ink">{data.frictionScore}%</span>
            <span className="text-[10px] text-ink-muted">Lower is better</span>
          </div>
          <div className="mt-3 h-1.5 w-full bg-warm-inset rounded-full overflow-hidden">
            <div 
              className={clsx(
                "h-full rounded-full transition-all duration-1000",
                data.frictionScore < 30 ? "bg-emerald-500" : data.frictionScore < 50 ? "bg-amber-500" : "bg-rose-500"
              )}
              style={{ width: `${data.frictionScore}%` }}
            />
          </div>
        </div>

        <div 
          className="rounded-2xl border p-5 bg-white shadow-sm"
          style={{ borderColor: WARM_BORDER }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-ink-soft">Value Multiplier</p>
            <DollarSign size={14} className="text-emerald-500" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-ink">${data.valuePerAction}</span>
            <span className="text-[10px] text-ink-muted">Per Action</span>
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-ink-faint">
            Illustrative per-action assumption — not a measured ROI for your org.{' '}
            <Link
              to="/dashboard/behavioral-roi-methodology"
              className="text-brand-teal font-medium underline underline-offset-2"
            >
              Methodology &amp; citations
            </Link>
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 p-3 bg-brand-teal/5 rounded-xl border border-brand-teal/10 text-[10px] text-brand-teal leading-relaxed">
        <Info size={14} className="shrink-0 mt-0.5" />
        <p>
          <strong>ROI Insight:</strong> Your team is building durable habits at Scale. 
          Current trajectory suggests a {(100 - data.frictionScore)}% "Success Probability" for new behavioral goals this month.
        </p>
      </div>
    </div>
  )
}
